// Confirmation modal for material delete + restore actions (single
// row + bulk). Reuses the same shape as
// `ManufacturingInventoryTemplateConfirm` so the inventory panel
// continues to feel coherent without dragging in a generic confirm
// primitive yet (deferred until a third caller materializes).
//
// Design notes:
// - Variant `delete` paints the primary button amber and copies in
//   the restore-window reassurance from `mfg_material_delete_with_restore.md`.
// - Variant `restore` paints the primary button emerald to signal a
//   non-destructive action.
// - The body is templated by the caller so a single bulk-action
//   surface can fan out the same modal for "Delete 1 material" /
//   "Delete 14 materials" / "Restore 14 materials".
import type { ReactNode } from "react";
import { ghostBtnClass, mfgConfirmDialogClass } from "@/components/manufacturing/manufacturingSurfaceClasses";

type Variant = "delete" | "restore";

type Props = {
  open: boolean;
  variant: Variant;
  title: string;
  /** Body copy. Pass a `<p>` or fragment so the caller can include
   *  inline emphasis without us having to template every shape. */
  body: ReactNode;
  /** Primary button label. Defaults reasonable per-variant strings
   *  but a caller can override (e.g. "Delete 14 materials"). */
  confirmLabel?: string;
  busy?: boolean;
  onCancel: () => void;
  onConfirm: () => void;
};

const dangerBtnClass =
  "rounded-lg bg-amber-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-amber-700 disabled:cursor-not-allowed disabled:opacity-60";

const restoreBtnClass =
  "rounded-lg bg-emerald-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-60";

export function ManufacturingMaterialDeleteConfirm({
  open,
  variant,
  title,
  body,
  confirmLabel,
  busy = false,
  onCancel,
  onConfirm,
}: Props) {
  if (!open) return null;
  const primaryClass = variant === "delete" ? dangerBtnClass : restoreBtnClass;
  const fallbackLabel = variant === "delete" ? "Delete" : "Restore";
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="mfg-material-delete-confirm-title"
      onClick={busy ? undefined : onCancel}
    >
      <div
        className={`max-w-sm ${mfgConfirmDialogClass}`}
        onClick={(e) => e.stopPropagation()}
      >
        <h3
          id="mfg-material-delete-confirm-title"
          className="text-base font-semibold text-gray-900 dark:text-zinc-100"
        >
          {title}
        </h3>
        <div className="text-sm text-gray-600 dark:text-zinc-400">{body}</div>
        <div className="flex justify-end gap-2 pt-1">
          <button
            type="button"
            className={ghostBtnClass}
            onClick={onCancel}
            disabled={busy}
          >
            Cancel
          </button>
          <button
            type="button"
            className={primaryClass}
            onClick={onConfirm}
            disabled={busy}
            autoFocus
          >
            {busy ? "Working…" : confirmLabel ?? fallbackLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
